import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLocationArrow, faSpinner } from '@fortawesome/free-solid-svg-icons'
import JobSearchField from './JobSearchField'
import type { AssetOrInventory, JobTreadJobOption } from './types'
import { formatJobLabel } from './types'
import { useAutoDismiss } from './useAutoDismiss'

type InventoryCheckToProps = {
  item: AssetOrInventory
  onCancel: () => void
  onConfirm: (job: JobTreadJobOption) => Promise<void>
}

export const InventoryCheckTo: React.FC<InventoryCheckToProps> = ({ item, onCancel, onConfirm }) => {
  const [job, setJob] = useState<JobTreadJobOption | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const current = formatJobLabel(item)
  useAutoDismiss(error, () => setError(null))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!job?.id && !job?.number && !job?.name) {
      setError('Select a location')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onConfirm(job)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to check to location')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="w-full max-w-md mx-auto flex flex-col gap-4" onSubmit={(e) => void submit(e)}>
      <div className="text-center space-y-1">
        <h4 className="font-bold text-xl flex items-center justify-center gap-2">
          <FontAwesomeIcon icon={faLocationArrow} />
          Check to
        </h4>
        <p className="text-sm text-base-content/70">{item.name}</p>
        {current ? (
          <p className="text-xs text-base-content/60">
            Currently at: <span className="font-medium text-base-content">{current}</span>
          </p>
        ) : (
          <p className="text-xs text-base-content/60">No location yet</p>
        )}
      </div>

      <JobSearchField
        label="Location"
        value={job}
        onChange={setJob}
        required
        disabled={saving}
        placeholder="Search JobTread locations..."
      />

      {error ? <div className="alert alert-error py-2 text-sm">{error}</div> : null}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving || !job}>
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : null}
          {saving ? 'Saving...' : 'Confirm'}
        </button>
      </div>
    </form>
  )
}

export default InventoryCheckTo
